import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import { NationsLeagueFaqItem } from "../types";

interface NationsLeagueFaqAccordionsProps {
  faq: NationsLeagueFaqItem[];
}

export const NationsLeagueFaqAccordions: React.FC<NationsLeagueFaqAccordionsProps> = ({ faq }) => {
  // Group by category, keeping the order in which categories first appear
  const groups: { category: string; items: NationsLeagueFaqItem[] }[] = [];
  faq.forEach((item) => {
    const category = (item.category || "").trim();
    let group = groups.find((g) => g.category === category);
    if (!group) {
      group = { category, items: [] };
      groups.push(group);
    }
    group.items.push(item);
  });

  return (
    <>
      {groups.map((group, gIdx) => (
        <React.Fragment key={gIdx}>
          {group.category && (
            <Typography variant="h6" sx={{ fontWeight: 700, mt: gIdx === 0 ? 0 : 3, mb: 1 }}>
              {group.category}
            </Typography>
          )}
          {group.items.map((item, idx) => (
            <Accordion key={idx} disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>{item.question}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography sx={{ whiteSpace: "pre-wrap" }}>{item.answer}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </React.Fragment>
      ))}
    </>
  );
};

interface NationsLeagueFaqDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  faq: NationsLeagueFaqItem[];
  pageUrl: string;
}

const NationsLeagueFaqDialog: React.FC<NationsLeagueFaqDialogProps> = ({
  open,
  onClose,
  title,
  faq,
  pageUrl,
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle>FAQ — {title}</DialogTitle>
    <DialogContent dividers>
      {faq.length === 0 ? (
        <Typography color="text.secondary">No questions have been added yet.</Typography>
      ) : (
        <NationsLeagueFaqAccordions faq={faq} />
      )}
    </DialogContent>
    <DialogActions>
      <Button href={pageUrl}>Open full page</Button>
      <Button onClick={onClose} variant="contained">
        Close
      </Button>
    </DialogActions>
  </Dialog>
);

export default NationsLeagueFaqDialog;
